import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  Badge,
  Flex,
  SimpleGrid,
  Button,
  Skeleton,
  SkeletonText,
  Divider
} from '@chakra-ui/react';
import { API_BASE_URL } from '../../config/api_keys';

export default function ProductDetails() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/product/${id}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || json.error || 'Failed to load');
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const computeDiscount = (mrp, sell, stored) => {
    if (typeof stored === 'number') return stored.toFixed(0);
    if (mrp && sell && mrp > 0) {
      const d = ((mrp - sell) / mrp) * 100;
      return Math.max(0, Math.min(100, d)).toFixed(0);
    }
    return null;
  };

  if (loading) {
    return (
      <Container maxW="container.lg" py={8}>
        <Skeleton height="300px" mb={6} />
        <SkeletonText noOfLines={6} spacing="4" />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxW="container.lg" py={8} textAlign="center">
        <Heading size="md" mb={2}>Product not found</Heading>
        <Text color="gray.600" mb={4}>{error || 'This product does not exist.'}</Text>
        <Button as={Link} to="/stores" colorScheme="green">Back to Stores</Button>
      </Container>
    );
  }

  const product = data.product || {};
  const title = product.title || product.name || 'Product';
  const images = product.images || [product.productpic1 || product.imageUrl || (product.images && product.images[0])].filter(Boolean);
  const mrp = product.mrp ?? product?.variants?.[0]?.mrp;
  const sell = product.sellingPrice ?? product?.variants?.[0]?.sellingPrice;
  const discount = computeDiscount(mrp, sell, product.discount ?? product?.variants?.[0]?.discount);

  return (
    <Container maxW="container.lg" py={8}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
        <Box>
          {images.length > 0 ? (
            <Image
              src={images[0]}
              alt={title}
              borderRadius="md"
              objectFit="cover"
              w="100%"
              fallbackSrc="https://via.placeholder.com/800x600?text=No+Image"
            />
          ) : (
            <Box
              height="400px"
              bg="gray.100"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="gray.500">No image available</Text>
            </Box>
          )}
          
          {/* Image Gallery */}
          {images.length > 1 && (
            <SimpleGrid columns={4} spacing={2} mt={4}>
              {images.slice(1).map((img, index) => (
                <Image
                  key={index + 1}
                  src={img}
                  alt={`${title} ${index + 2}`}
                  borderRadius="md"
                  objectFit="cover"
                  height="80px"
                  cursor="pointer"
                  _hover={{ opacity: 0.8 }}
                  fallbackSrc="https://via.placeholder.com/80x80?text=Image"
                />
              ))}
            </SimpleGrid>
          )}
        </Box>
        <Box>
          <Heading mb={2}>{title}</Heading>
          {discount !== null && (
            <Badge colorScheme="red" borderRadius="full" px={3} py={1} mb={3}>{discount}% OFF</Badge>
          )}
          {mrp && (
            <Flex align="baseline" gap={3} mb={3}>
              <Text fontSize="2xl" fontWeight="bold">₹{sell ?? product.price}</Text>
              <Text as="s" color="gray.500">₹{mrp}</Text>
            </Flex>
          )}
          {product.description && (
            <Text color="gray.700" mb={4}>{product.description}</Text>
          )}

          {product.usageType && (
            <Text color="gray.700" mb={2}><b>Usage:</b> {product.usageType}</Text>
          )}
          {product.targetDiseaseOrCrop && (
            <Text color="gray.700" mb={2}><b>Target:</b> {product.targetDiseaseOrCrop}</Text>
          )}

          {product.category && (
            <Text color="gray.700" mb={2}><b>Category:</b> {product.category.name || product.category}</Text>
          )}

          {Array.isArray(product.variants) && product.variants.length > 0 && (
            <Box mt={4}>
              <Heading size="sm" mb={2}>Variants</Heading>
              <Flex gap={2} wrap="wrap">
                {product.variants.map((v, i) => (
                  <Badge key={i} px={3} py={1}>{v.name} — ₹{v.sellingPrice} <Text as="span" color="gray.500">(MRP ₹{v.mrp})</Text></Badge>
                ))}
              </Flex>
            </Box>
          )}

          <Divider my={6} />
          <Button as={Link} to="/stores" colorScheme="green">Back to Stores</Button>
        </Box>
      </SimpleGrid>
    </Container>
  );
}